/* eslint-disable camelcase */
/** @jsxImportSource @emotion/react */
import { lighten } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Buttons, TextFields } from '@zip/business-components';
import * as Icons from '@zip/icons';
import parse from 'autosuggest-highlight/parse';
import { Country } from 'enums';
import { theme } from 'global';
import throttle from 'lodash/throttle';
import {
  FC,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { renderFieldError } from 'utils/validators';
import { Address, AddressErrors } from 'types';
import {
  AddressLookupInlineProps,
  InlineAddress,
} from './AddressLookupInlineProps';
import * as styles from './AddressLookupInline.styles';

function loadScript(
  src: string,
  position: HTMLElement | null,
  id: string
): void {
  if (!position) {
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
}

type AutocompleteService = google.maps.places.AutocompleteService;
type AutocompletePrediction = google.maps.places.AutocompletePrediction;
type PlacesService = google.maps.places.PlacesService;
type PlaceResult = google.maps.places.PlaceResult;

let autocompleteService: { current: AutocompleteService };
let placesService: { current: PlacesService };

const stateOptions = [
  { value: 'ACT', label: 'ACT' },
  { value: 'QLD', label: 'QLD' },
  { value: 'NSW', label: 'NSW' },
  { value: 'NT', label: 'NT' },
  { value: 'SA', label: 'SA' },
  { value: 'TAS', label: 'TAS' },
  { value: 'VIC', label: 'VIC' },
  { value: 'WA', label: 'WA' },
];

const emptyAddress: Required<Omit<InlineAddress, 'country'>> = {
  unitNumber: '',
  streetNumber: '',
  streetName: '',
  suburb: '',
  postcode: '',
  state: '',
};

const buildInitialManualValues = (address?: InlineAddress): InlineAddress => ({
  ...emptyAddress,
  ...(address ?? {}),
});

const validateManualAddress = (values: InlineAddress): AddressErrors => {
  const errors: AddressErrors = {};

  if (!values.streetName) {
    errors.streetName = 'Please enter a street name';
  }
  if (!values.streetNumber) {
    errors.streetNumber = 'Please enter a street number';
  }
  if (!values.suburb) {
    errors.suburb = 'Please enter a suburb';
  }
  if (!values.postcode) {
    errors.postcode = 'Please enter a postcode';
  } else if (!/^[0-9]{4}$/i.test(values.postcode)) {
    errors.postcode = 'Please enter a valid postcode';
  }
  if (!values.state) {
    errors.state = 'Please select a state';
  }

  return errors;
};

const formatOneLineAddressWithoutCountry = (address: InlineAddress): string =>
  [
    address.unitNumber
      ? `${address.unitNumber}/${address.streetNumber}`
      : address.streetNumber,
    address.streetName,
    address.suburb,
    [address.state, address.postcode].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');

const formatStreetLine = (address: InlineAddress): string =>
  [
    address.unitNumber
      ? `${address.unitNumber}/${address.streetNumber}`
      : address.streetNumber,
    address.streetName,
  ]
    .filter(Boolean)
    .join(' ');

const formatSecondaryLine = (address: InlineAddress): string =>
  [
    address.suburb,
    [address.state, address.postcode].filter(Boolean).join(' '),
    address.country,
  ]
    .filter(Boolean)
    .join(', ');

const ManualAddressFields = ({
  address,
  onChange,
  showValidationErrors = false,
}: {
  address?: InlineAddress;
  onChange: (values: InlineAddress) => void;
  showValidationErrors?: boolean;
}): JSX.Element => {
  const [values, setValues] = useState<InlineAddress>(() =>
    buildInitialManualValues(address)
  );
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const errors = validateManualAddress(values);

  useEffect(() => {
    if (!showValidationErrors) {
      return;
    }

    setTouched({
      streetName: true,
      streetNumber: true,
      unitNumber: true,
      suburb: true,
      postcode: true,
      state: true,
    });
  }, [showValidationErrors]);

  useEffect(() => {
    onChange(values);
  }, [values, onChange]);

  const setFieldValue = (field: keyof InlineAddress, value: string): void => {
    setValues((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const setFieldTouched = (field: keyof InlineAddress): void => {
    setTouched((current) => ({
      ...current,
      [field]: true,
    }));
  };

  const fieldError = (field: keyof AddressErrors): string | undefined =>
    touched[field] ? errors[field] : undefined;

  return (
    <div css={styles.manualAddressRoot}>
      <div css={styles.manualAddressFields}>
        <TextFields.Outlined
          type="text"
          name="streetName"
          label="Street name *"
          css={[styles.textfield, styles.fullWidthField]}
          onChange={(event): void =>
            setFieldValue('streetName', event.target.value)
          }
          onBlur={(): void => setFieldTouched('streetName')}
          value={values.streetName}
          error={Boolean(fieldError('streetName'))}
          helperText={renderFieldError(
            touched['streetName'],
            errors.streetName
          )}
          slotProps={{ formHelperText: { className: 'body4' } }}
        />
        <TextFields.Outlined
          type="text"
          name="streetNumber"
          label="Street number *"
          css={[styles.textfield, styles.fullWidthField]}
          onChange={(event): void =>
            setFieldValue('streetNumber', event.target.value)
          }
          onBlur={(): void => setFieldTouched('streetNumber')}
          value={values.streetNumber}
          error={Boolean(fieldError('streetNumber'))}
          helperText={renderFieldError(
            touched['streetNumber'],
            errors.streetNumber
          )}
          slotProps={{ formHelperText: { className: 'body4' } }}
        />
        <TextFields.Outlined
          type="text"
          name="unitNumber"
          label="Unit or apartment number"
          css={[styles.textfield, styles.fullWidthField]}
          onChange={(event): void =>
            setFieldValue('unitNumber', event.target.value)
          }
          onBlur={(): void => setFieldTouched('unitNumber')}
          value={values.unitNumber}
          error={Boolean(fieldError('unitNumber'))}
          helperText={renderFieldError(
            touched['unitNumber'],
            errors.unitNumber
          )}
          slotProps={{ formHelperText: { className: 'body4' } }}
        />
        <TextFields.Outlined
          type="text"
          name="suburb"
          label="Suburb *"
          css={[styles.textfield, styles.fullWidthField]}
          onChange={(event): void =>
            setFieldValue('suburb', event.target.value)
          }
          onBlur={(): void => setFieldTouched('suburb')}
          value={values.suburb}
          error={Boolean(fieldError('suburb'))}
          helperText={renderFieldError(touched['suburb'], errors.suburb)}
          slotProps={{ formHelperText: { className: 'body4' } }}
        />
        <TextFields.Outlined
          type="text"
          name="postcode"
          label="Postcode *"
          css={styles.textfield}
          onChange={(event): void =>
            setFieldValue('postcode', event.target.value)
          }
          onBlur={(): void => setFieldTouched('postcode')}
          value={values.postcode}
          error={Boolean(fieldError('postcode'))}
          helperText={renderFieldError(touched['postcode'], errors.postcode)}
          slotProps={{ formHelperText: { className: 'body4' } }}
        />
        <TextFields.Select
          name="state"
          label="State *"
          css={styles.textfield}
          options={stateOptions}
          slotProps={{
            select: {
              onChange: (event): void =>
                setFieldValue('state', event.target.value as string),
              onBlur: (): void => setFieldTouched('state'),
              value: values.state,
            },
            formHelperText: { className: 'body4' },
          }}
          error={Boolean(fieldError('state'))}
          helperText={renderFieldError(touched['state'], errors.state)}
        />
      </div>
    </div>
  );
};

const REQUIRED_ADDRESS_FIELDS = [
  'streetNumber',
  'streetName',
  'suburb',
  'state',
  'postcode',
] as const;

const hasCompleteAddress = (address: Record<string, string>): boolean =>
  REQUIRED_ADDRESS_FIELDS.every((field) => Boolean(address[field]));

const AUTO_RESOLVE_MAX_RETRIES = 3;
const AUTO_RESOLVE_RETRY_DELAY_MS = 300;

const AddressLookupInline: FC<AddressLookupInlineProps> = ({
  onBlur,
  label,
  value,
  error,
  helperText,
  handleChange,
  name,
  manualAddress,
  autoResolve = false,
}) => {
  const autocompleteId = `${name}-google-places-autocomplete`;
  const [autoCompleteValue, setAutoCompleteValue] =
    useState<AutocompletePrediction>({
      description: value ?? '',
      matched_substrings: [],
      place_id: '',
      structured_formatting: null,
      terms: [],
      types: [],
    });
  const [inputValue, setInputValue] = useState<string>(value ?? '');
  const [options, setOptions] = useState<AutocompletePrediction[]>([]);
  const loaded = useRef(false);
  const [manualEntryOpen, setManualEntryOpen] = useState<boolean>(false);
  const [selectedAddress, setSelectedAddress] = useState<InlineAddress>();
  const autoResolveAttempted = useRef(false);
  const autoResolveRetryCount = useRef(0);

  useEffect(() => {
    const nextValue = value ?? '';

    setAutoCompleteValue({
      description: nextValue,
      matched_substrings: [],
      place_id: '',
      structured_formatting: null,
      terms: [],
      types: [],
    });
    setInputValue(nextValue);
    // Reset auto-resolve when value changes externally (e.g. user clears field)
    if (!nextValue) {
      autoResolveAttempted.current = false;
      autoResolveRetryCount.current = 0;
    }
  }, [value]);

  useEffect(() => {
    if (!autoResolve) return;
    if (autoResolveAttempted.current) return;
    if (!inputValue) return;
    // If already resolved (user selected a prediction with a real place_id), skip
    if (autoCompleteValue.place_id) return;

    const attempt = (): void => {
      // Initialise services if Google script has loaded but services not yet set
      if (!autocompleteService?.current && (window as any).google) {
        autocompleteService = {
          current: new (window as any).google.maps.places.AutocompleteService(),
        };
        placesService = {
          current: new (window as any).google.maps.places.PlacesService(
            document
              .getElementById(autocompleteId)
              ?.appendChild(document.createElement('div')) ??
              document.createElement('div')
          ),
        };
      }

      // Services still not ready — retry after a short delay
      if (!autocompleteService?.current) {
        if (autoResolveRetryCount.current < AUTO_RESOLVE_MAX_RETRIES) {
          autoResolveRetryCount.current += 1;
          window.setTimeout(attempt, AUTO_RESOLVE_RETRY_DELAY_MS);
        } else {
          // Google never loaded — signal failure so parent can show hint
          handleChange(null, null);
        }
        return;
      }

      autoResolveAttempted.current = true;

      autocompleteService.current.getPlacePredictions(
        {
          input: inputValue,
          componentRestrictions: {
            country: [Country.Australia, Country.NewZealand],
          },
        },
        (results) => {
          const top = results?.[0];
          if (!top?.place_id) {
            handleChange(null, null);
            return;
          }

          placesService.current.getDetails(
            { placeId: top.place_id, fields: ['address_components'] },
            (details: PlaceResult) => {
              if (!details?.address_components) {
                handleChange(null, null);
                return;
              }

              let parsedAddress: Record<string, string> = {};
              details.address_components.forEach((component) => {
                if (component.types[0] !== 'administrative_area_level_2') {
                  const attribute = parseGoogleAttribute(component.types[0]);
                  parsedAddress = {
                    ...parsedAddress,
                    [attribute]:
                      component.types[0] === 'administrative_area_level_1'
                        ? component.short_name
                        : component.long_name,
                  };
                }
              });

              if (!hasCompleteAddress(parsedAddress)) {
                handleChange(null, null);
                return;
              }

              const resolvedAddress = Object.assign(
                {},
                parsedAddress
              ) as unknown as Address;
              setAutoCompleteValue(top);
              setSelectedAddress(resolvedAddress);
              handleChange(null, resolvedAddress);
            }
          );
        }
      );
    };

    attempt();
  }, [autoResolve, inputValue]);

  if (typeof window !== 'undefined' && !loaded.current) {
    if (!document.querySelector('#google-maps')) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API_KEY}&libraries=places`,
        document.querySelector('head'),
        'google-maps'
      );
    }

    loaded.current = true;
  }

  const fetchPlacePredictions = useMemo(
    () =>
      throttle(
        (
          request: { input: string },
          callback: (results?: AutocompletePrediction[]) => void
        ) => {
          autocompleteService.current.getPlacePredictions(
            {
              ...request,
              componentRestrictions: {
                country: [Country.Australia, Country.NewZealand],
              },
            },
            callback
          );
        },
        200
      ),
    []
  );

  useEffect(() => {
    let active = true;

    if (!autocompleteService?.current && (window as any).google) {
      autocompleteService = {
        current: new (window as any).google.maps.places.AutocompleteService(),
      };
      placesService = {
        current: new (window as any).google.maps.places.PlacesService(
          document
            .getElementById(autocompleteId)
            ?.appendChild(document.createElement('div')) ??
            document.createElement('div')
        ),
      };
    }
    if (!autocompleteService?.current) {
      return undefined;
    }

    if (inputValue === '') {
      setOptions(autoCompleteValue ? [autoCompleteValue] : []);
      return undefined;
    }
    fetchPlacePredictions(
      { input: inputValue },
      (results?: AutocompletePrediction[]) => {
        if (active) {
          let newOptions = [] as AutocompletePrediction[];

          if (autoCompleteValue) {
            newOptions = [autoCompleteValue];
          }

          if (results) {
            newOptions = [...newOptions, ...results];
          }

          setOptions(newOptions);
        }
      }
    );

    return (): void => {
      active = false;
    };
  }, [autoCompleteValue, autocompleteId, inputValue, fetchPlacePredictions]);

  const syncManualAddress = useCallback(
    (nextAddress: InlineAddress): void => {
      const newAddress = formatOneLineAddressWithoutCountry(nextAddress);

      setAutoCompleteValue({
        description: newAddress,
        structured_formatting: {
          main_text: formatStreetLine(nextAddress),
          secondary_text: formatSecondaryLine(nextAddress),
          main_text_matched_substrings: [
            {
              offset: 0,
              length: 0,
            },
          ],
        },
        matched_substrings: [],
        place_id: '',
        terms: [],
        types: [],
      });

      setInputValue(newAddress);
      handleChange(null, nextAddress);
    },
    [handleChange]
  );

  function parseGoogleAttribute(attribute: string): string {
    let output;
    switch (attribute) {
      case 'subpremise':
        output = 'unitNumber';
        break;

      case 'street_number':
        output = 'streetNumber';
        break;

      case 'route':
        output = 'streetName';
        break;
      case 'locality':
        output = 'suburb';
        break;

      case 'postal_code':
        output = 'postcode';
        break;
      case 'administrative_area_level_1':
        output = 'state';
        break;
      default:
        output = attribute;
        break;
    }
    return output;
  }

  const handleAutoCompleteChange = async (event, newValue): Promise<void> => {
    if (newValue) {
      const newPlace = newValue as AutocompletePrediction;

      placesService.current.getDetails(
        {
          placeId: newPlace.place_id,
          fields: ['address_components'],
        },
        (details: PlaceResult) => {
          let parsedAddress = {};
          details.address_components?.forEach((component) => {
            if (component.types[0] !== 'administrative_area_level_2') {
              const attribute = parseGoogleAttribute(component.types[0]);
              parsedAddress = {
                ...parsedAddress,
                [attribute]:
                  component.types[0] === 'administrative_area_level_1'
                    ? component.short_name
                    : component.long_name,
              };
            }
          });
          const newAddress = Object.assign({}, parsedAddress) as Address;
          setSelectedAddress(newAddress);
          handleChange(null, newAddress);
        }
      );
      setAutoCompleteValue(newPlace);
      setOptions(newValue ? [newPlace, ...options] : options);
    } else {
      setAutoCompleteValue(newValue as AutocompletePrediction);
      setOptions(newValue ? [...options] : options);
    }
  };

  const handleManualEntryClick = (event: MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    setManualEntryOpen(true);
  };

  const manualEntryButton = (
    <Buttons.Text
      type="button"
      css={styles.link}
      onClick={handleManualEntryClick}
    >
      enter address manually
    </Buttons.Text>
  );

  if (manualEntryOpen) {
    return (
      <ManualAddressFields
        address={selectedAddress ?? manualAddress}
        showValidationErrors={error}
        onChange={syncManualAddress}
      />
    );
  }

  return (
    <Autocomplete
      id={autocompleteId}
      getOptionLabel={(option): string =>
        typeof option === 'string' ? option : (option.description ?? '')
      }
      filterOptions={(x): AutocompletePrediction[] => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={autoCompleteValue}
      inputValue={inputValue ?? ''}
      forcePopupIcon={false}
      disableClearable
      freeSolo
      onChange={handleAutoCompleteChange}
      onInputChange={(event, newInputValue): void => {
        setInputValue(newInputValue);
        // Only invalidate structured fields when the change comes from a real user
        // interaction (event !== null). Programmatic syncs from the value prop fire
        // onInputChange with event === null and must not clear a resolved address.
        if (event !== null && selectedAddress) {
          // Just mark the address as no longer valid, but keep the input value
          // so the user can continue typing. Don't notify parent of clearing.
          setSelectedAddress(undefined);
        }
      }}
      slotProps={{
        paper: {
          sx: {
            border: `solid 1px ${lighten(theme.colors.neutral[300], 0.8)}`,
            borderRadius: '4px',
            boxShadow: `0px 0px 4px 1px ${lighten(theme.colors.black, 0.85)}`,
          },
        },
      }}
      renderInput={(params): JSX.Element => {
        const { InputLabelProps, InputProps, ...restParams } = params;
        return (
          <TextFields.Outlined
            {...restParams}
            name={name}
            label={label}
            css={styles.helperText}
            className="fs-mask"
            variant="outlined"
            fullWidth
            error={error}
            onBlur={(focus): void => {
              if (!focus.target.value) {
                // undefined = user cleared the field; null is reserved for auto-resolve failure
                handleChange(null, undefined);
              }
              onBlur(focus);
            }}
            helperText={
              error && helperText ? (
                <>
                  <ErrorOutlineIcon className="errorIcon" aria-hidden />
                  {helperText}&nbsp;or&nbsp;{manualEntryButton}
                </>
              ) : (
                <>Search address or&nbsp;{manualEntryButton}</>
              )
            }
            slotProps={{
              input: InputProps,
              inputLabel: InputLabelProps,
              formHelperText: { className: 'body4' },
            }}
          />
        );
      }}
      renderOption={(props, option: AutocompletePrediction): JSX.Element => {
        if (option.structured_formatting ?? false) {
          const matches =
            option.structured_formatting.main_text_matched_substrings;
          const parts = parse(
            option.structured_formatting.main_text,
            matches.map((match: any) => [
              match.offset,
              match.offset + match.length,
            ])
          );

          return (
            <li {...props}>
              <Grid container sx={{ flexGrow: 1 }} alignItems="center">
                <Grid>
                  <Icons.MapPin
                    css={{
                      color: theme.colors.neutral[300],
                      marginRight: 8,
                    }}
                  />
                </Grid>
                <Grid size="grow">
                  {parts.map((part, index) => (
                    <span
                      key={index}
                      style={{ fontWeight: part.highlight ? 700 : 400 }}
                    >
                      {part.text}
                    </span>
                  ))}
                  <Typography variant="body2" color="textSecondary">
                    {option.structured_formatting.secondary_text}
                  </Typography>
                </Grid>
              </Grid>
            </li>
          );
        }

        return <li {...props}></li>;
      }}
    />
  );
};

export default AddressLookupInline;
